import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI, api } from '../services/api';
import { isAzureADEnabled } from '../config/authConfig';
import { queryClient } from '../lib/queryClient';
import { setAuthenticatedUser, clearAuthenticatedUser, trackUserSessionLog } from '../utils/appInsights';
import { createSession, removeSession, clearAllSessions, getSession } from '../utils/sessionTracker';
import { clearMsalSessionStorage } from '../utils/msalSessionStorage';

export interface UserPreferences {
  timezone: string;
  dateFormat: 'MM/dd/yyyy' | 'dd/MM/yyyy' | 'yyyy-MM-dd' | 'dd-MMM-yyyy';
  timeFormat: '12H' | '24H';
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  roleId: string;
  role: string;  // Backend returns role name as string for backward compatibility
  roleType: 'GOD' | 'SYSTEM' | 'CUSTOMER' | 'CUSTOM';  // Flat field from backend
  roleLevel: number;  // Flat field from backend (0-4)
  class?: 'contact' | 'user';
  customerId?: string;
  customerName?: string;
  engagementId?: string;
  engagementName?: string;
  preferences?: UserPreferences;
  isServiceDeskUser?: boolean;  // True if user belongs to a Service Desk group
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithAzureAD: (accessToken: string, id_token?: string) => Promise<void>;
  register: (data: any) => Promise<void>;
  logout: (logoutType?: 'USER_LOGOUT' | 'IDLE_TIMEOUT_LOGOUT') => Promise<void>;
  updateUser: (user: User) => void;
  updatePreferences: (preferences: Partial<UserPreferences>) => Promise<void>;
  hasRole: (roleName: string) => boolean;
  hasAnyRole: (roleNames: string[]) => boolean;
  hasMinimumLevel: (minLevel: number) => boolean;
  isAdmin: () => boolean;
  isSuperAdmin: () => boolean;
  isContact: () => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  // Helper function to get user role info for logging
  const getUserRoleInfo = (userData: User) => {
    return {
      firstName: userData.firstName,
      lastName: userData.lastName,
      role: userData.role,
      roleType: userData.roleType,
      roleLevel: userData.roleLevel,
      userClass: userData.class,
      customerId: userData.customerId,
      customerName: userData.customerName,
      engagementId: userData.engagementId,
      engagementName: userData.engagementName,
      isServiceDeskUser: userData.isServiceDeskUser,
    };
  };

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        try {
          const response = await authAPI.getMe();
          const user = response.data.user;
          setUser(user);
          // Set authenticated user context for Application Insights
          setAuthenticatedUser(user.email || user.firstName || user.id || 'Unknown');
          
          const userId = user.email || user.id || 'Unknown';
          const userName = user.email || user.firstName || user.id || 'Unknown';
          
          // Check if session already exists (to prevent duplicate logs on page refresh)
          const existingSession = getSession(userId);
          
          if (!existingSession) {
            // Only create session and log login if no existing session exists
            // This prevents duplicate login logs on page refresh
            const sessionId = createSession(userId);
            
            trackUserSessionLog(
              'User Login',
              userName,
              {
                action: 'User Login',
                loginMethod: 'session_restore',
              },
              {
                ...getUserRoleInfo(user),
                sessionId,
              }
            );
          }
          // If session exists, do nothing (user already logged in, just restoring state)
        } catch (error) {
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          // Clear authenticated user context
          clearAuthenticatedUser();
          // Clear all sessions for this user on auth error
          const userId = localStorage.getItem('lastUserId') || 'Unknown';
          clearAllSessions(userId);
        }
      } else {
        // Clear authenticated user context if no token
        clearAuthenticatedUser();
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authAPI.login({ email, password });
      
      // Check if password reset is required
      if (response.data.requirePasswordChange) {
        // Navigate to password reset page with the reset token
        navigate('/password-reset', { 
          state: { 
            resetToken: response.data.resetToken,
            email: response.data.user.email
          }
        });
        return;
      }

      const { accessToken, refreshToken, user } = response.data;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      // Clear React Query cache to remove any stale data from previous user session
      // This ensures switching between users (e.g., admin -> contact or contact -> user)
      // immediately shows correct filtered data without requiring a browser refresh
      queryClient.clear();

      // Fetch fresh user data from /auth/me to ensure we have latest info
      // This ensures changes made to user (role, class, customerId, etc.) are immediately reflected
      let updatedUser: User;
      try {
        const meResponse = await authAPI.getMe();
        updatedUser = meResponse.data.user;
        setUser(updatedUser);
        // Set authenticated user context for Application Insights
        setAuthenticatedUser(updatedUser.email || updatedUser.firstName || updatedUser.id || 'Unknown');
      } catch (error) {
        // Fallback to user from login response if /auth/me fails
        updatedUser = user;
        setUser(user);
        // Set authenticated user context for Application Insights
        setAuthenticatedUser(user.email || user.firstName || user.id || 'Unknown');
      }

      // Create session and log login
      const userId = updatedUser.email || updatedUser.id || 'Unknown';
      const sessionId = createSession(userId);
      const userName = updatedUser.email || updatedUser.firstName || updatedUser.id || 'Unknown';
      
      // Store userId for potential cleanup on error
      localStorage.setItem('lastUserId', userId);
      
      trackUserSessionLog(
        'User Login',
        userName,
        {
          action: 'User Login',
          loginMethod: 'email',
        },
        {
          ...getUserRoleInfo(updatedUser),
          sessionId,
        }
      );

      navigate(updatedUser.class === 'contact' ? '/dashboard' : '/kpi-dashboard');
    } catch (error: any) {
      throw error.response?.data || error;
    }
  };

  const loginWithAzureAD = async (accessToken: string, id_token?: string) => {
    try {
      const response = await authAPI.exchangeAzureToken({ accessToken, id_token });
      const { accessToken: appToken, refreshToken, user } = response.data;

      localStorage.setItem('accessToken', appToken);
      localStorage.setItem('refreshToken', refreshToken);

      // Clear React Query cache for Azure AD login as well
      queryClient.clear();

      // Fetch fresh user data from /auth/me to ensure we have latest info
      let updatedUser: User;
      try {
        const meResponse = await authAPI.getMe();
        updatedUser = meResponse.data.user;
        setUser(updatedUser);
        // Set authenticated user context for Application Insights
        setAuthenticatedUser(updatedUser.email || updatedUser.firstName || updatedUser.id || 'Unknown');
      } catch (error) {
        // Fallback to user from exchange response if /auth/me fails
        updatedUser = user;
        setUser(user);
        // Set authenticated user context for Application Insights
        setAuthenticatedUser(user.email || user.firstName || user.id || 'Unknown');
      }

      // Create session and log login
      const userId = updatedUser.email || updatedUser.id || 'Unknown';
      const sessionId = createSession(userId);
      const userName = updatedUser.email || updatedUser.firstName || updatedUser.id || 'Unknown';
      
      // Store userId for potential cleanup on error
      localStorage.setItem('lastUserId', userId);
      
      trackUserSessionLog(
        'User Login',
        userName,
        {
          action: 'User Login',
          loginMethod: 'azure_ad',
        },
        {
          ...getUserRoleInfo(updatedUser),
          sessionId,
        }
      );

      navigate(updatedUser.class === 'contact' ? '/dashboard' : '/kpi-dashboard');
    } catch (error: any) {
      console.error('Azure AD login failed:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Azure AD authentication failed';
      
      if (error.response?.status === 401) {
        errorMessage = 'Invalid Azure AD token. Please try signing in again.';
      } else if (error.response?.status === 403) {
        errorMessage = error.response?.data?.message || 'Access denied. Please ensure you have the correct permissions in Azure AD.';
      } else if (error.response?.status === 503) {
        errorMessage = 'Azure AD service is currently unavailable. Please try again later.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      throw new Error(errorMessage);
    }
  };

  const register = async (data: any) => {
    try {
      await authAPI.register(data);
      // After registration, redirect to login
      navigate('/login', { 
        state: { 
          message: 'Registration successful! Please login with your credentials.',
          email: data.email 
        } 
      });
    } catch (error: any) {
      throw error.response?.data || error;
    }
  };

  const logout = async (logoutType: 'USER_LOGOUT' | 'IDLE_TIMEOUT_LOGOUT' = 'USER_LOGOUT') => {
    // Get user info before clearing (for logging)
    const currentUser = user;
    const userId = currentUser?.email || currentUser?.id || 'Unknown';
    const userName = currentUser?.email || currentUser?.firstName || currentUser?.id || 'Unknown';

    // Remove session and calculate active time
    const sessionInfo = removeSession(userId);
    const activeTime = sessionInfo?.activeTime || 0;
    const sessionId = sessionInfo?.sessionId || '';

    // Log logout before clearing user data, but only if we have a valid sessionId
    if (currentUser && sessionId) {
      trackUserSessionLog(
        'User Logout',
        userName,
        {
          action: logoutType === 'IDLE_TIMEOUT_LOGOUT' ? 'IDLE_TIMEOUT_LOGOUT' : 'USER_LOGOUT',
        },
        {
          ...getUserRoleInfo(currentUser),
          sessionId,
          activeTime, // Active time in milliseconds
        }
      );
    }

    try {
      await authAPI.logout();
    } catch (error) {
      // Continue with logout even if API call fails
    }

    // Clear React Query cache on logout to prevent data leakage between users
    queryClient.clear();

    // Clear local storage
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('itsm_last_activity_timestamp');
    setUser(null);
    
    // Clear authenticated user context for Application Insights
    clearAuthenticatedUser();
    
    // Clear all sessions for this user (cleanup)
    if (userId !== 'Unknown') {
      clearAllSessions(userId);
    }
    
    // Clear stored userId
    localStorage.removeItem('lastUserId');

    // Clear all MSAL-related sessionStorage entries to prevent interference with future logins
    // This ensures switching between Azure AD and local accounts works correctly
    clearMsalSessionStorage();

    // Handle Azure AD logout if available and enabled
    if (isAzureADEnabled && window.location.pathname !== '/login') {
      // Import MSAL instance dynamically to avoid hooks rule violations
      import('../components/MsalProviderWrapper').then(({ msalInstance }) => {
        if (msalInstance) {
          // Clear all MSAL accounts locally without popup
          // We've already cleared sessionStorage above, so just clear the MSAL cache
          const accounts = msalInstance.getAllAccounts();
          accounts.forEach(_account => {
            msalInstance.setActiveAccount(null);
          });
          // Clear MSAL cache completely
          try {
            msalInstance.clearCache();
          } catch (e) {
            // clearCache may not be available in all MSAL versions, ignore errors
          }
        }
        navigate('/login');
      }).catch(() => {
        // If MSAL is not available, just navigate to login
        navigate('/login');
      });
    } else {
      navigate('/login');
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const updatePreferences = async (preferences: Partial<UserPreferences>) => {
    try {
      const response = await api.patch('/users/me/preferences', preferences);
      if (user) {
        setUser({
          ...user,
          preferences: {
            ...user.preferences,
            ...response.data.preferences,
          } as UserPreferences,
        });
      }
    } catch (error) {
      console.error('Failed to update preferences:', error);
      throw error;
    }
  };

  const hasRole = (roleName: string): boolean => {
    // Backend returns role as string for backward compatibility
    return typeof user?.role === 'string' ? user.role === roleName : false;
  };

  const hasAnyRole = (roleNames: string[]): boolean => {
    // Backend returns role as string for backward compatibility
    return typeof user?.role === 'string' ? roleNames.includes(user.role) : false;
  };

  const hasMinimumLevel = (minLevel: number): boolean => {
    // Backend returns roleLevel as flat field
    return (user?.roleLevel ?? 0) >= minLevel;
  };

  const isAdmin = (): boolean => {
    // Backend returns roleLevel as flat field
    return (user?.roleLevel ?? 0) >= 3; // admin or super_admin
  };

  const isSuperAdmin = (): boolean => {
    // Backend returns roleType as flat field
    return user?.roleType === 'GOD';
  };

  const isContact = (): boolean => {
    return user?.class === 'contact';
  };

  const value = {
    user,
    loading,
    login,
    loginWithAzureAD,
    register,
    logout,
    updateUser,
    updatePreferences,
    hasRole,
    hasAnyRole,
    hasMinimumLevel,
    isAdmin,
    isSuperAdmin,
    isContact,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};